import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useCampaign } from '../contexts/CampaignContext';
import { getCouponByCode, hasMarketingScreen } from '../services/couponService';
import { UseDetectCouponOptions } from '../types/campaign';
import { useUser } from '../contexts/UserContext';

/**
 * Custom hook to detect and handle coupon query parameters
 * Automatically fetches coupon data and shows campaign modal when appropriate
 */
export function useCouponDetection(options: UseDetectCouponOptions = {}) {
    const {
        enabled = true,
        onSuccess,
        onError,
        userHasDevices = false,
    } = options;
    const router = useRouter();
    const { user, logout } = useUser();
    const {
        setCouponData,
        setOriginalCouponCode,
        setFromCampaign,
        setShowCampaignModal,
        setIsLoadingCoupon,
        setCouponError,
        setHasShownModal,
        couponData,
        fromCampaign,
        hasShownModal,
        isLoadingCoupon,
    } = useCampaign();

    // Track which coupon we're currently fetching to prevent duplicates
    const fetchingCouponRef = useRef<string | null>(null);
    // Track the last processed coupon to prevent re-processing
    const lastProcessedCouponRef = useRef<string | null>(null);

    useEffect(() => {
        // Only run on client side and when enabled
        if (!enabled || typeof window === 'undefined') {
            return;
        }

        // Only run when router is ready
        if (!router.isReady) {
            return;
        }

        const couponParam = router.query.coupon as string;

        // If no coupon parameter, clear any existing campaign data
        if (!couponParam) {
            if (fromCampaign || couponData) {
                console.log(
                    '[COUPON-DETECTION] No coupon parameter, clearing campaign data'
                );
                setCouponData(null);
                setOriginalCouponCode(null);
                setFromCampaign(false);
                setShowCampaignModal(false);
                setCouponError(null);
                lastProcessedCouponRef.current = null;
            }
            return;
        }

        // Check if we've already processed this exact coupon
        if (lastProcessedCouponRef.current === couponParam) {
            console.log(
                '[COUPON-DETECTION] Already processed this coupon:',
                couponParam
            );
            return;
        }

        // If we already have this coupon data, don't fetch again
        if (couponData && couponData.name === couponParam) {
            console.log(
                '[COUPON-DETECTION] Already have data for this coupon, skipping fetch'
            );
            // Mark as processed
            lastProcessedCouponRef.current = couponParam;
            // But ensure modal is shown if we have marketing screen data and haven't shown it yet
            if (!hasShownModal && hasMarketingScreen(couponData)) {
                // Skip showing modal if user already has devices (completed signup flow)
                if (userHasDevices) {
                    console.log(
                        '[COUPON-DETECTION] Skipping modal - user already has devices paired'
                    );
                    setHasShownModal(true); // Mark as shown so we don't retry
                    return;
                }
                console.log(
                    '[COUPON-DETECTION] Showing modal for existing coupon data'
                );
                setShowCampaignModal(true);
            }
            return;
        }

        // If we're already loading this specific coupon, don't start another request
        if (isLoadingCoupon || fetchingCouponRef.current === couponParam) {
            console.log(
                '[COUPON-DETECTION] Already loading coupon, skipping duplicate request'
            );
            return;
        }

        console.log(
            '[COUPON-DETECTION] Coupon parameter detected:',
            couponParam
        );

        // Mark that we're fetching this coupon
        fetchingCouponRef.current = couponParam;

        // Start loading
        setIsLoadingCoupon(true);
        setCouponError(null);

        // Fetch coupon data
        getCouponByCode(couponParam)
            .then((response) => {
                setIsLoadingCoupon(false);
                fetchingCouponRef.current = null;
                // Mark as processed
                lastProcessedCouponRef.current = couponParam;

                if (response.success && response.data) {
                    console.log(
                        '[COUPON-DETECTION] Coupon data fetched successfully:',
                        response.data
                    );

                    // Store coupon data and the original coupon code from query param
                    setCouponData(response.data);
                    setOriginalCouponCode(couponParam);
                    setFromCampaign(true);

                    // Show modal if we have marketing screen data
                    if (hasMarketingScreen(response.data)) {
                        // Skip showing modal if user already has devices (completed signup flow)
                        if (userHasDevices) {
                            console.log(
                                '[COUPON-DETECTION] Marketing screen found but skipping modal - user already has devices'
                            );
                            setHasShownModal(true); // Mark as shown so we don't retry
                        } else {
                            console.log(
                                '[COUPON-DETECTION] Marketing screen data found, showing modal'
                            );
                            setShowCampaignModal(true);
                        }
                    } else {
                        console.log(
                            '[COUPON-DETECTION] No marketing screen data, modal will not be shown'
                        );
                    }

                    // Call success callback
                    onSuccess?.(response.data);
                } else {
                    console.error(
                        '[COUPON-DETECTION] Failed to fetch coupon:',
                        response.error
                    );

                    // Check if authentication is required
                    if (response.requiresAuth) {
                        console.log(
                            '[COUPON-DETECTION] JWT expired, logging out user and redirecting to re-auth'
                        );

                        // Clear the user session
                        logout();

                        // Store the email if we have it for re-authentication
                        if (user?.email) {
                            localStorage.setItem('reauth_email', user.email);
                        }

                        // Redirect to email step with a message
                        setCouponError(
                            'Your session has expired. Please sign in again.'
                        );

                        // Note: The index.tsx will handle showing the email step when user is logged out
                    } else {
                        setCouponError(
                            response.error || 'Failed to load campaign data'
                        );
                    }

                    // Call error callback
                    onError?.(response.error || 'Failed to load campaign data');
                }
            })
            .catch((error) => {
                console.error(
                    '[COUPON-DETECTION] Error fetching coupon:',
                    error
                );
                setIsLoadingCoupon(false);
                fetchingCouponRef.current = null;
                // Still mark as processed even on error to prevent retry loops
                lastProcessedCouponRef.current = couponParam;
                setCouponError(
                    error.message ||
                        'An error occurred while loading campaign data'
                );

                // Call error callback
                onError?.(
                    error.message ||
                        'An error occurred while loading campaign data'
                );
            });
    }, [
        router.isReady,
        router.query.coupon,
        enabled,
        userHasDevices,
        // Only include callbacks that are stable (wrapped in useCallback in parent)
        onSuccess,
        onError,
    ]);

    return {
        couponParam: router.query.coupon as string,
        hasCouponParam: !!router.query.coupon,
    };
}

export default useCouponDetection;
